/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import BottomPartForm from './BottomPartForm';
import { cryptoModel } from './Models';
import { cryptoValidation } from './ModelValidators';
import i18nContext from 'components/i18n-context';
import { amountFormattedValue, prepareFieldErrors, replaceSpaces, strToFloat } from 'services/utils';
import Input from 'uikit/Input/Input';

const CryptoWithdrawalForm = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  uploadDocuments,
  setIban,
  ibanCredentials,
  account,
  isRepeatPayment,
  paymentMethod,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  isCommissionLoading,
  isLoading,
  isSuccess,
  validationProps,
  isFormResetAfterAccountChanging,
  setIsFormResetAfterAccountChanging,
  validateCryptoWalletAddress,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction
}) => {
  const i18n = useContext(i18nContext);
  const model = cryptoModel;
  const validationSchema = cryptoValidation({ ...validationProps, model });
  const [disabled, setDisabled] = useState(true);
  const [showMethodCommission, setShowMethodCommission] = useState(false);
  const [hasWalletValidationError, setHasWalletValidationError] = useState(false);
  const [walletValidationError, setWalletValidationError] = useState(null);
  const [lastValidatedWallet, setLastValidatedWallet] = useState('');
  const [isWalletValidationPending, setIsWalletValidationPending] = useState(false);
  const isValidatingWallet = useRef(false);

  // Use wallet validation error if it exists, otherwise use the general error
  const effectiveError = walletValidationError || error;
  const serverFieldsErrors = prepareFieldErrors(i18n, effectiveError);
  const form = useFormik(validationSchema);
  const { values, errors, handleSubmit, handleChange, validateField, validateForm, resetForm } = form;

  useEffect(() => {
    const asyncOperations = async () => {
      if (isRepeatPayment) {
        await handleRecipientWalletComplete(true);
      } else {
        resetForm();
        setIsFormResetAfterAccountChanging(true);
      }
    };

    asyncOperations();
  }, [account.wallet_number]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  // Track error changes to detect wallet validation failures
  useEffect(() => {
    // If we were waiting for validation result and error updated
    if (isWalletValidationPending) {
      if (
        error?.code === 'CRYPTO_ADDRESS_CHECK_FAILED' ||
        error?.fields?.some((field) => field.field === 'recipientWallet')
      ) {
        // Wallet validation error detected
        setHasWalletValidationError(true);
        setWalletValidationError(error);
      } else {
        // No wallet error - validation passed
        setHasWalletValidationError(false);
        setWalletValidationError(null);
      }
      // Clear pending state
      setIsWalletValidationPending(false);
    } else if (
      error?.code === 'CRYPTO_ADDRESS_CHECK_FAILED' ||
      error?.fields?.some((field) => field.field === 'recipientWallet')
    ) {
      // Error came from somewhere else (not from our validation)
      setHasWalletValidationError(true);
      setWalletValidationError(error);
    }
  }, [error, isWalletValidationPending]);

  useEffect(() => {
    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      // Don't calculate commission if there's a wallet validation error
      if (values.amount && paymentMethod && !hasWalletValidationError) {
        await calculateCommission(customerNumber, {
          walletNumber: account?.wallet_number,
          paymentMethod: paymentMethod,
          amount: strToFloat(values.amount?.toString() || '')
        });
      }

      if (isRepeatPayment && !values.recipient_wallet && !isForApprovalTransactionEdit && !hasWalletValidationError) {
        await handleCheckTransfer(undefined);
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging]);

  const handleCheckTransfer = async (fieldName) => {
    // Don't proceed if there's a wallet validation error or if wallet is being validated
    if (hasWalletValidationError || isValidatingWallet.current) return;

    if (fieldName) await validateField(fieldName);
    if (values.recipient_wallet && values.amount) {
      const errorsResult = await validateForm();
      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            data: { wallet: account, paymentMethod, ...ibanCredentials }
          })
        );
        setShowMethodCommission(true);
        setDisabled(false);
      }
    }
  };

  const onAmountIBANChange = (e) => {
    setShowMethodCommission(false);
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
      setHasWalletValidationError(false); // Reset error when user changes the wallet
      setWalletValidationError(null); // Clear the saved error
      setLastValidatedWallet(''); // Clear last validated wallet to force revalidation
    }
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }
    return `${commission.commissionDetails?.displayAmount || amountFormattedValue(commission.value)}`;
  };

  const handleRecipientWalletComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');

    if (values.recipient_wallet) {
      // Only validate if wallet has changed since last validation
      if (values.recipient_wallet !== lastValidatedWallet) {
        // Set flags that we're validating
        isValidatingWallet.current = true;
        setIsWalletValidationPending(true);

        // Store current wallet being validated
        const validatingWallet = values.recipient_wallet;

        await validateCryptoWalletAddress({
          senderWalletNumber: account?.wallet_number,
          recipientCryptoWalletAddress: replaceSpaces(values.recipient_wallet),
          recipientCryptoNetwork: account?.network
        });

        // Mark validation as complete
        isValidatingWallet.current = false;
        setLastValidatedWallet(validatingWallet);

        // Keep pending state - it will be cleared by useEffect when error updates
        // Don't call handleCheckTransfer here
        return;
      }
    }

    // Only call handleCheckTransfer if not validating and no error
    if (
      causedOnBlur &&
      !isForApprovalTransactionEdit &&
      !isValidatingWallet.current &&
      !hasWalletValidationError &&
      !isWalletValidationPending
    ) {
      await handleCheckTransfer(undefined);
    }
  };

  const bottomPartProps = {
    values,
    errors,
    error: effectiveError,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    currency: account.currency,
    currencyPrecision: account?.currency_precision,
    paymentMethod,
    paymentProvider: account.transfer_provider,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!effectiveError ||
      hasWalletValidationError ||
      !paymentMethod,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model,
    hasWalletValidationError,
    walletValidationError,
    validateWallet: handleRecipientWalletComplete,
    lastValidatedWallet
  };

  return (
    <>
      <div className={'inputs-wrapper'}>
        <Input
          isRequired={true}
          autoComplete={'off'}
          label={i18n.getMessage('transfer.form.recipientWallet.label')}
          name={'recipient_wallet'}
          value={values.recipient_wallet}
          error={!values.account_number && errors.recipient_wallet}
          onChange={onAmountIBANChange}
          onBlur={() => handleRecipientWalletComplete(true)}
          subText={
            ibanCredentials?.bank_name &&
            i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
          }
        />
      </div>
      <BottomPartForm {...bottomPartProps} isCryptoForm={true} />
    </>
  );
};

CryptoWithdrawalForm.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  setIban: PropTypes.func,
  setIsNonIban: PropTypes.func,
  ibanCredentials: PropTypes.object,
  clearIbanCredentials: PropTypes.func,
  checkIban: PropTypes.func.isRequired,
  isRepeatPayment: PropTypes.bool,
  account: PropTypes.object,
  paymentMethod: PropTypes.string,
  resetPaymentMethods: PropTypes.func,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  isSuccess: PropTypes.bool,
  currency: PropTypes.string,
  validationProps: PropTypes.object.isRequired,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsFormResetAfterAccountChanging: PropTypes.func,
  setIsPreparedTransactionsCreation: PropTypes.func,
  validateCryptoWalletAddress: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func
};

export default CryptoWithdrawalForm;
